/**
 * Admin → Clients list.
 *
 * Two-pane: organisations grouped with their contracts. Internal
 * staff get the read+write surface — clients shouldn't reach this
 * route at all (the App route gate handles that).
 *
 * Header carries two CTAs:
 *   - "New organisation" → modal (this slice)
 *   - "New contract" appears per-org → modal (next slice)
 *
 * Org row clicks open the org detail screen at /admin/clients/:orgId.
 * Contract row clicks open contract detail.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, FileSignature, Plus } from 'lucide-react';

import type { Contract, Organization } from '@beacon/core';

import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { useContracts } from '@/hooks/use-contracts.js';
import { useOrganizations } from '@/hooks/use-organizations.js';

import { NewOrganizationDialog } from './NewOrganizationDialog.js';
import { NewContractDialog } from './NewContractDialog.js';

export function AdminClientsList() {
  const navigate = useNavigate();
  const orgsQuery = useOrganizations();
  const contractsQuery = useContracts();
  const [newOrgOpen, setNewOrgOpen] = useState(false);
  const [newContractForOrg, setNewContractForOrg] = useState<
    Organization | null
  >(null);

  if (orgsQuery.isLoading || contractsQuery.isLoading) {
    return <AdminClientsListSkeleton />;
  }
  if (orgsQuery.isError || contractsQuery.isError) {
    return <div className="p-8 text-ink-300">Couldn't load clients.</div>;
  }

  const orgs = orgsQuery.data ?? [];
  const contracts = contractsQuery.data ?? [];

  // Group contracts by organizationId for fast render.
  const contractsByOrg = new Map<string, Contract[]>();
  for (const c of contracts) {
    const arr = contractsByOrg.get(c.organizationId) ?? [];
    arr.push(c);
    contractsByOrg.set(c.organizationId, arr);
  }

  // Sort: client orgs first (they're what ops cares about), then internal.
  const sortedOrgs = [...orgs].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'client' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="h-full overflow-y-auto bg-ink-900">
      <header className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[12px] mono text-ink-300 uppercase tracking-[0.06em]">
              Admin
            </div>
            <h1 className="text-[19px] font-semibold text-ink-100 mt-0.5">
              Clients
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/admin/clients/new-contract')}
              data-testid="wizard-launch"
            >
              <FileSignature size={13} />
              New contract
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setNewOrgOpen(true)}
            >
              <Plus size={13} />
              New organisation
            </Button>
          </div>
        </div>
      </header>

      <div className="p-6 space-y-4">
        {sortedOrgs.length === 0 ? (
          <div className="rounded-md border border-ink-600 bg-ink-850 p-8 text-center text-ink-300">
            No organisations yet. Add the first one with the button above.
          </div>
        ) : (
          sortedOrgs.map((org) => {
            const cs = contractsByOrg.get(org.id) ?? [];
            return (
              <section
                key={org.id}
                className="rounded-md border border-ink-600 bg-ink-850 overflow-hidden"
              >
                <header className="px-4 py-3 border-b border-ink-600 flex items-center gap-3">
                  <Building2 size={16} className="text-ink-300 shrink-0" />
                  <Link
                    to={`/admin/clients/${encodeURIComponent(org.id)}`}
                    className="text-[14px] font-medium text-ink-100 hover:text-beacon-300 focus-ring rounded"
                  >
                    {org.name}
                  </Link>
                  <Badge tone={org.type === 'internal' ? 'slate' : 'blue'}>
                    {org.type.toUpperCase()}
                  </Badge>
                  <span className="ml-auto flex items-center gap-2">
                    <span className="text-[11.5px] text-ink-400 mono">
                      {cs.length} contract{cs.length === 1 ? '' : 's'}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setNewContractForOrg(org)}
                    >
                      <Plus size={11} />
                      Contract
                    </Button>
                  </span>
                </header>
                {cs.length === 0 ? (
                  <div className="px-4 py-3 text-[12.5px] text-ink-400">
                    No contracts yet for {org.name}.
                  </div>
                ) : (
                  <ul className="divide-y divide-ink-700">
                    {cs.map((c) => (
                      <li key={c.id}>
                        <Link
                          to={`/admin/clients/${encodeURIComponent(org.id)}/contracts/${encodeURIComponent(c.id)}`}
                          className="flex items-center gap-3 px-4 py-2.5 text-[13px] hover:bg-ink-800/60 focus-ring"
                        >
                          <FileSignature
                            size={14}
                            className="text-ink-300 shrink-0"
                          />
                          <span className="text-ink-100 font-medium truncate">
                            {c.name}
                          </span>
                          <Badge
                            tone={
                              c.type === 'sla'
                                ? 'blue'
                                : c.type === 'warranty'
                                  ? 'violet'
                                  : c.type === 'bau'
                                    ? 'green'
                                    : 'slate'
                            }
                          >
                            {c.type.toUpperCase()}
                          </Badge>
                          <Badge
                            tone={
                              c.status === 'active'
                                ? 'green'
                                : c.status === 'expired'
                                  ? 'yellow'
                                  : 'neutral'
                            }
                          >
                            {c.status}
                          </Badge>
                          <span className="ml-auto text-[12px] mono text-ink-400">
                            {c.startDate} → {c.endDate}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })
        )}
      </div>

      <NewOrganizationDialog
        open={newOrgOpen}
        onClose={() => setNewOrgOpen(false)}
      />
      <NewContractDialog
        organization={newContractForOrg}
        onClose={() => setNewContractForOrg(null)}
      />
    </div>
  );
}

function AdminClientsListSkeleton() {
  return (
    <div className="h-full overflow-y-auto bg-ink-900" aria-busy="true">
      <header className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850">
        <Skeleton className="h-2.5 w-12 mb-1" />
        <Skeleton className="h-5 w-24" />
      </header>
      <div className="p-6 space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <section
            key={i}
            className="rounded-md border border-ink-600 bg-ink-850 overflow-hidden"
          >
            <header className="px-4 py-3 border-b border-ink-600 flex items-center gap-3">
              <Skeleton className="h-4 w-4 rounded" inline />
              <Skeleton className="h-3.5 w-40" inline />
              <Skeleton className="h-5 w-12" inline />
              <Skeleton className="h-3 w-16 ml-auto" inline />
            </header>
            <ul className="divide-y divide-ink-700">
              {Array.from({ length: 2 }).map((__, j) => (
                <li
                  key={j}
                  className="flex items-center gap-3 px-4 py-2.5"
                >
                  <Skeleton className="h-3.5 w-3.5 rounded" inline />
                  <Skeleton className="h-3 w-44" inline />
                  <Skeleton className="h-5 w-12" inline />
                  <Skeleton className="h-5 w-14" inline />
                  <Skeleton className="h-3 w-32 ml-auto" inline />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
